/* eslint-disable no-console */
import {SearchOutlined, ExclamationOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import {TF} from '../../constants/Common';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import machineDetails from '../../services/modules/IP/machineAPI';
import deviceFuelAPI from '../../services/modules/JSON/deviceFuel';
import deviceTypeAPI from '../../services/modules/JSON/deviceType';
import fuelTypeAPI from '../../services/modules/JSON/fuelType';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import DisplayModal from '../DisplayModal/DisplayModal';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import DropdownConfigProvider from '../DrawerForm/Utils/DropdownConfigProvider';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {FuelKey, FuelLabel} from '../Forms/Constants/IP/FuelKeyLabel';
import FuelForm from '../Forms/FuelForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import {returnFilter} from './Utils/filterReturn';
import sumValue from './Utils/sumValue';
import TableTitleHeader from './Utils/TableTitleHeader';
import TimeFrameDataTooltip from './Utils/TimeFrameDataTooltip';

const FuelTable: React.FC<{
  selectedYear: string;
  facilityId: string;
}> = ({selectedYear, facilityId}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    fuelType: number;
    numberOfUnit: number;
    deviceType: number;
    fuelUsed: number;
    quantity: number;
    cost: number;
    comment?: string;
  }

  const {efdbData} = useContext(SelectedCountryContext);

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const [openDisplayModal, setOpenDisplayModal] = useState(false);
  const [openDisplayModalData, setOpenDisplayModalData] = useState({});
  const [openDisplayModalType, setOpenDisplayModalType] = useState({
    title: '',
    dataKey: ''
  });
  const [facilityEfdb, setFacilityEfdb] = useState<any>({});

  const [showModelInfo, setShowModelInfo] = useState(false);

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      machineDetails(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
          setFacilityEfdb(
            efdbData?.[selectedYear]?.find((i: any) => {
              return i?.facilityId === facilityId;
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([deviceFuelAPI(), fuelTypeAPI(), deviceTypeAPI()])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      machineDetails(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    machineDetails(APITypeEnum.DELETE_ALL, {
      machines: _id
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value, record) => {
        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                .toString()
                .toLowerCase()
                .includes(value.toString().toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toString().toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };
  const {fuelType, deviceType, deviceFuel} = useJSONStore.getState();

  const machineKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key
    },
    {
      key: FuelKey.DEVICE_TYPE,
      label: FuelLabel.DEVICE_TYPE,
      type: objTypes.json,
      format: deviceType
    },
    {
      key: FuelKey.NO_UNITS,
      label: FuelLabel.NO_UNITS,
      type: objTypes.number
    },
    {
      key: FuelKey.FUEL_TYPE,
      label: FuelLabel.FUEL_TYPE,
      type: objTypes.json,
      format: fuelType
    },
    {
      key: FuelKey.FUEL_USED,
      label: FuelLabel.FUEL_USED,
      type: objTypes.json,
      format: deviceFuel
    },
    {
      key: TF,
      label: [FuelLabel.QUANTITY_TF, FuelLabel.COST_TF],
      type: objTypes.timeFrameData,
      renderType: 'multi'
    },
    ...(hasUserRole(UserRole.Checker_Role) ||
    hasUserRole(UserRole.Both_role) ||
    !hasUserRole(UserRole.Company_Admin)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  // const machineKeyLabelInfo = [
  //   {
  //     key: FuelKey.STATUS,
  //     label: FuelLabel.STATUS,
  //     type: 'string'
  //   },
  //   {
  //     key: FuelKey.COMMENT,
  //     label: FuelLabel.COMMENT,
  //     type: 'string'
  //   }
  // ];

  const columns: Array<defaultCol & ColumnsType<DataType>[0]> = useMemo(() => {
    return [
      {
        title: FuelLabel.DEVICE_TYPE,
        dataIndex: FuelKey.DEVICE_TYPE,
        type: 'select',
        ...SearchFilter(FuelLabel.DEVICE_TYPE, FuelKey.DEVICE_TYPE, deviceType),
        render: (value) => {
          return (
            <>
              {
                deviceType?.find((item) => {
                  return item?.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: FuelLabel.NO_UNITS,
        dataIndex: FuelKey.NO_UNITS,
        // width: '120px',
        editable: true,
        type: 'number',
        render: (value) => {
          return formatIndian(value);
        }
      },

      {
        title: FuelLabel.FUEL_TYPE,
        dataIndex: FuelKey.FUEL_TYPE,
        editable: true,
        type: 'select',
        filterer: fuelType,
        filters: fuelType.map((item) => {
          return {text: item.label, value: item.value};
        }),
        onFilter: (value, record) => {
          return record[FuelKey.FUEL_TYPE as keyof DataType] === value;
        },
        render: (value) => {
          return (
            <>
              {
                fuelType?.find((item) => {
                  return item?.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: FuelLabel.FUEL_USED,
        dataIndex: FuelKey.FUEL_USED,
        editable: true,
        // ...sorter(FuelKey.FUEL_USED as keyof DataType),
        // width: '100px',
        type: 'select',
        ...SearchFilter(FuelLabel.FUEL_USED, FuelKey.FUEL_USED, deviceFuel),
        render: (value: any) => {
          return (
            <>
              {
                deviceFuel?.find((item) => {
                  return (
                    item?.value === value &&
                    item?.dbName === facilityEfdb?.scope1
                  );
                })?.label
              }
            </>
          );
        }
      },
      {
        title: FuelLabel.QUANTITY_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // ...sorter(FuelKey.QUANTITY_TF as keyof DataType),
        onFilter: (value, record) => {
          return returnFilter(record, value);
        },
        render: (value: any) => {
          const mainData = value?.find((item: any) => {
            return item.type === FuelKey.QUANTITY_TF;
          });
          // return <TimeFrameRender value={value} dataKey={FuelKey.QUANTITY_TF} />;

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${FuelLabel.QUANTITY_TF} (${mainData?.unit ?? ''})`,
                  dataKey: FuelKey.QUANTITY_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${String(formatIndian(sumValue(mainData?.data)))} ${
                mainData?.unit ?? ''
              }`}</div>
            </div>
          );
        }
      },
      {
        title: FuelLabel.COST_TF,
        dataIndex: TF,
        editable: true,
        type: 'select',
        // render: (value) => {
        //   return <TimeFrameRender value={value} dataKey={FuelKey.COST_TF} />;
        // }
        render: (value) => {
          const mainData = value?.find((item: any) => {
            return item?.type === FuelKey.COST_TF;
          });

          return (
            <div
              onClick={() => {
                setOpenDisplayModal(true);
                setOpenDisplayModalData(mainData);
                setOpenDisplayModalType({
                  title: `${FuelLabel.COST_TF} (${mainData?.unit ?? ''})`,
                  dataKey: FuelKey.COST_TF
                });
              }}
              style={{
                textDecorationLine: 'underline',
                pointerEvents: 'auto',
                cursor: 'pointer'
              }}
            >
              <div>{`${mainData?.unit ?? ''} ${String(
                formatIndian(sumValue(mainData?.data))
              )}`}</div>
            </div>
          );
        }
      },

      ...(hasUserRole(UserRole.Maker_Role) ||
      hasUserRole(UserRole.Both_role) ||
      hasViewRole(UserRole.View_Only)
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label, // Ensure `FuelLabel.STATUS_COMMENTS` is defined
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  // <div
                  //   onClick={() => {
                  //     // setOpenDisplayModal(true);
                  //     // setOpenDisplayModalType();
                  //   }}
                  //   style={{
                  //     fontWeight: 700,
                  //     color:
                  //       value === checkerStatusKey.REJECTED
                  //         ? 'pink'
                  //         : value === checkerStatusKey.ACCEPTED
                  //           ? '#d3eec6'
                  //           : 'gray'
                  //   }}
                  // >
                  //   {value === checkerStatusKey.REJECTED
                  //     ? checkerStatusLabel.REJECTED
                  //     : value === checkerStatusKey.ACCEPTED
                  //       ? `${checkerStatusLabel.REJECTED}: ${
                  //           record?.comment ?? ''
                  //         }`
                  //       : checkerStatusLabel.IN_REVIEW}
                  // </div>
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []), //
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              fixed: 'right' as const,
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              fixed: 'right' as const,
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),

      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) => {
                return dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                  />
                ) : null; // Ensure null is explicitly returned when the condition fails
              }
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <DropdownConfigProvider>
      <div>
        <TableTitleHeader
          title="Machine Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Fuel'
          }}
          sampleDownload={{href: SampleExcel.fuelByMachine}}
          jobs={{setShowJobs, uploadType: uploadTypes.MachineUtility}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          shouldEdit={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
          }
        />
        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.MachineUtility}
          details={`${facilityId}/${selectedYear}`}
        />

        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  getCheckboxProps: (record: any) => ({
                    disabled: record.status === statusEm.ACCEPTED
                  }),
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item: any) => {
                        return item?.status !== statusEm.ACCEPTED
                          ? item._id
                          : null;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          // pagination={{pageSize: 10}}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
        />

        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          FormComponent={<FuelForm />}
          API={machineDetails}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          stepsTitle={[
            'Configure Details',
            'Machine Details',
            'Quantity & Cost Details'
          ]}
          formPreview={machineKeyLabel}
        />
        <DisplayModal
          title={openDisplayModalType.title}
          open={openDisplayModal}
          onCancel={() => {
            setOpenDisplayModal(false);
          }}
          displayComponent={
            <TimeFrameDataTooltip mainData={openDisplayModalData} />
          }
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        title="All Data"
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={machineKeyLabel} />
        }
        table={true}
        stepsTitle={['Status & Comments']}
        isChecker={true}
        API={machineDetails}
        isAction={false}
        // formPreview={machineKeyLabelInfo}
      />
    </DropdownConfigProvider>
  );
};

export default FuelTable;
